import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMedia } from 'react-use';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Drawer,
  IconButton,
} from '@c2fo/liquidity';
import {
  ArrowRightFromBracketIcon,
  CommentsQuestionIcon,
  FileCircleInfoIcon,
  XmarkIcon,
  CaretRightSolidIcon,
  LanguageIcon,
  MoneyBillWaveLightIcon,
} from '@c2fo/liquidity/icons';
import { getLanguage, SUPPORTED_LOCALES } from '@/i18n';
import { useIntercom } from '@/reporting/Intercom';
import { IntercomDataTargetNames } from '@/reporting/intercomDataTargetNames';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useUserDetails from '@/shared/data/useUserDetails';
import logout from '@/utils/logout';
import { useAgreementsUrl } from '@/utils/useAgreementsUrl';
import usePortalSync from '../usePortalSync';
import { CurrencyMenuItem, CurrencySubmenuOverlay } from './CurrencyMenuItem';
import { LanguageMenuItem, LanguageSubmenuOverlay } from './LanguageMenuItem';
import UserAvatar from './UserAvatar';
import UserInfoSection from './UserInfoSection';

interface UserAvatarMenuProps {
  variant: 'desktop' | 'mobile';
}

export type Submenu = 'language' | 'currency' | null;

export interface MenuItem {
  key: string;
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  href?: string;
  onClick?: () => void;
  external?: boolean;
}

export interface MenuSection {
  type: 'help' | 'legal';
  items: MenuItem[];
}

const UserAvatarMenuComponent = ({ variant }: UserAvatarMenuProps) => {
  const { i18n, t } = useTranslation();
  const { openChat } = useIntercom();
  const { privacyLink, termsOfUseLink } = useAgreementsUrl();
  const { data: userDetails } = useUserDetails();
  // desktop dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // mobile drawer state
  const isMobile = useMedia('(max-width: 768px)');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<Submenu>(null);
  const drawerDialogRef = useRef<HTMLDivElement>(null);
  const shouldShowPortal = usePortalSync(isDrawerOpen);
  const language = useMemo(() => getLanguage(i18n.language), [i18n.language]);
  const languageCode = SUPPORTED_LOCALES.find((locale) => locale.language === language)?.code;
  const accountName = userDetails.me?.account?.name ?? '';
  const firstName = userDetails.me?.personalInfo?.firstName ?? '';
  const lastName = userDetails.me?.personalInfo?.lastName ?? '';
  const name = [firstName, lastName].filter(Boolean).join(' ');
  const initials = [firstName?.charAt(0), lastName?.charAt(0)].filter(Boolean).join('').toUpperCase();

  const faqLink = `https://help.c2fo.com/${languageCode}`;

  const menuSections: MenuSection[] = useMemo(
    () => [
      {
        type: 'help' as const,
        items: [
          {
            key: 'chat',
            icon: CommentsQuestionIcon,
            label: t('navigation.chat'),
            onClick: openChat,
          },
          {
            key: 'faqs',
            icon: FileCircleInfoIcon,
            label: t('navigation.faqs'),
            href: faqLink,
            external: true,
          },
        ],
      },
      {
        type: 'legal' as const,
        items: [
          {
            key: 'privacy',
            label: t('core.privacyPolicy'),
            href: privacyLink,
            external: true,
          },
          {
            key: 'terms',
            label: t('footer.termsOfUseLinkText'),
            href: termsOfUseLink,
            external: true,
          },
        ],
      },
    ],
    [t, openChat, faqLink, privacyLink, termsOfUseLink]
  );

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setActiveSubmenu(null);
  };

  const openSubmenu = (submenu: Submenu) => {
    setActiveSubmenu(submenu);
  };

  const closeSubmenu = () => {
    setActiveSubmenu(null);
  };

  // close drawer when viewport changes from mobile to desktop
  useEffect(() => {
    if (!isMobile && isDrawerOpen && variant === 'mobile') {
      // blur any focused element inside the drawer before closing to prevent aria-hidden warning
      if (document.activeElement instanceof HTMLElement) {
        const drawer = drawerDialogRef.current;

        if (drawer && drawer.contains(document.activeElement)) {
          document.activeElement.blur();
        }
      }

      closeDrawer();
    }
  }, [isMobile, isDrawerOpen, variant]);

  // close dropdown when viewport changes from desktop to mobile
  useEffect(() => {
    if (isMobile && isDropdownOpen && variant === 'desktop') {
      setIsDropdownOpen(false);
    }
  }, [isMobile, isDropdownOpen, variant]);

  if (variant === 'desktop') {
    return (
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <button
            aria-label={t('core.userMenu')}
            className="flex size-11 shrink-0 items-center justify-center rounded-full bg-purple-500 font-bold"
            type="button"
            {...(!isMobile && { 'data-intercom-target': 'avatarMenu' satisfies IntercomDataTargetNames })}
          >
            {initials}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-[calc(100vh_-_5rem)] w-72 overflow-y-auto">
          <UserInfoSection initials={initials} accountName={accountName} name={name} variant="desktop" />
          <DropdownMenuSeparator />
          <DropdownMenuLabel>{t('navigation.configurations')}</DropdownMenuLabel>
          <LanguageMenuItem />
          <CurrencyMenuItem />
          <DropdownMenuSeparator />
          {menuSections.map((section) => (
            <div key={section.type}>
              <DropdownMenuLabel>{t(`navigation.${section.type}`)}</DropdownMenuLabel>
              {section.items.map((item) => (
                <DropdownMenuItem key={item.key} className="flex items-center gap-2" onClick={item.onClick}>
                  {item.icon && <item.icon className="size-6 shrink-0 fill-text-secondary" />}
                  {item.href ? (
                    <a rel="noreferrer" target="_blank" href={item.href}>
                      {item.label}
                    </a>
                  ) : (
                    item.label
                  )}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </div>
          ))}
          <DropdownMenuItem className="flex items-center gap-2" onClick={() => logout()}>
            <ArrowRightFromBracketIcon className="size-6 shrink-0 fill-text-secondary" />
            {t('core.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <>
      <button aria-label={t('core.userMenu')} className="shrink-0" onClick={() => setIsDrawerOpen(true)} type="button">
        <UserAvatar initials={initials} size="md" />
      </button>
      <Drawer
        backdropClassName="z-10"
        dialogClassName="z-10 w-full max-w-80"
        hideCloseButton
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        portalContainerRef={drawerDialogRef}
        title={
          <div className="grid w-full grid-cols-1">
            <UserInfoSection
              initials={initials}
              accountName={accountName}
              name={name}
              variant="mobile"
              onSettingsClick={closeDrawer}
            />
            <div className="absolute right-0 top-0 z-20 bg-white pr-2 pt-2">
              <IconButton icon={XmarkIcon} name="close" onClick={closeDrawer} variant="ancillary" />
            </div>
          </div>
        }
        titleClassName="border-b p-0"
      >
        <div className="flex flex-col py-2">
          <div className="px-4 py-2 text-sm text-text-secondary">{t('navigation.configurations')}</div>
          <button
            className="flex items-center justify-between px-4 py-2 transition-colors hover:bg-lightBlue-50 focus:bg-lightBlue-50"
            onClick={() => openSubmenu('language')}
            type="button"
          >
            <div className="flex items-center gap-2 text-left">
              <LanguageIcon className="size-6 shrink-0 fill-text-secondary" />
              {t('core.preferredLanguage')}
            </div>
            <CaretRightSolidIcon className="size-4 shrink-0 fill-text-secondary" />
          </button>
          <button
            className="flex items-center justify-between px-4 py-2 transition-colors hover:bg-lightBlue-50 focus:bg-lightBlue-50"
            onClick={() => openSubmenu('currency')}
            type="button"
          >
            <div className="flex items-center gap-2 text-left">
              <MoneyBillWaveLightIcon className="size-6 shrink-0 fill-text-secondary" />
              {t('core.preferredCurrency')}
            </div>
            <CaretRightSolidIcon className="size-4 shrink-0 fill-text-secondary" />
          </button>
          <div className="my-2 h-[0.5px] bg-stroke" />
          {menuSections.map((section) => (
            <div key={section.type}>
              <div className="break-words px-4 py-2 text-sm text-text-secondary">{t(`navigation.${section.type}`)}</div>
              {section.items.map((item) => {
                const content = (
                  <>
                    {item.icon && <item.icon className="size-6 shrink-0 fill-text-secondary" />}
                    {item.label}
                  </>
                );

                if (item.href) {
                  return (
                    <a
                      key={item.key}
                      className="flex w-full items-center gap-2 px-4 py-2 transition-colors duration-200 hover:bg-lightBlue-50"
                      href={item.href}
                      onClick={closeDrawer}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {content}
                    </a>
                  );
                }

                return (
                  <button
                    key={item.key}
                    className="flex w-full items-center gap-2 px-4 py-2 transition-colors duration-200 hover:bg-lightBlue-50 focus:bg-lightBlue-50"
                    onClick={() => {
                      item.onClick?.();
                      closeDrawer();
                    }}
                    type="button"
                  >
                    {content}
                  </button>
                );
              })}
              <div className="my-2 h-[0.5px] bg-stroke" />
            </div>
          ))}
          <button
            className="flex items-center gap-2 px-4 py-2 transition-colors hover:bg-lightBlue-50 focus:bg-lightBlue-50"
            onClick={() => {
              closeDrawer();
              logout();
            }}
            type="button"
          >
            <ArrowRightFromBracketIcon className="size-6 shrink-0 fill-text-secondary" />
            {t('core.logout')}
          </button>
        </div>
      </Drawer>
      <LanguageSubmenuOverlay
        isOpen={activeSubmenu === 'language'}
        onBack={closeSubmenu}
        onClose={closeDrawer}
        portalContainer={drawerDialogRef.current}
        shouldShowPortal={shouldShowPortal}
      />
      <CurrencySubmenuOverlay
        isOpen={activeSubmenu === 'currency'}
        onBack={closeSubmenu}
        onClose={closeDrawer}
        portalContainer={drawerDialogRef.current}
        shouldShowPortal={shouldShowPortal}
      />
    </>
  );
};

const UserAvatarMenu = (props: UserAvatarMenuProps) => (
  <QueryBoundaries>
    <UserAvatarMenuComponent {...props} />
  </QueryBoundaries>
);

export default UserAvatarMenu;
